import React, { useState, useEffect } from "react";
import { Globe, Plus, Trash2, Bell, BellOff, Edit2, Save, X, DollarSign } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const AdminCalendarTab = ({
  holidays,
  blackoutDates,
  bookings,
  bbqBookings,
  showHolidayForm,
  setShowHolidayForm,
  holidayData,
  setHolidayData,
  showFetchApiForm,
  setShowFetchApiForm,
  fetchApiYear,
  setFetchApiYear,
  fetchingFromApi,
  showBlackoutForm,
  setShowBlackoutForm,
  blackoutData,
  setBlackoutData,
  blackoutConflicts,
  setBlackoutConflicts,
  onAddHoliday,
  onDeleteHoliday,
  onAddBlackout,
  onDeleteBlackout,
  onFetchHolidaysFromApi,
  onToggleBlackoutFacility,
}) => {
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotifForm, setShowNotifForm] = useState(false);
  const [notifForm, setNotifForm] = useState({ message: "", facility: "both" });
  const [editingNotif, setEditingNotif] = useState(null);
  const [editForm, setEditForm] = useState({ message: "", facility: "both" });

  // Pricing config state
  const [pricingConfig, setPricingConfig] = useState({ weekday_day: 90, weekday_night: 90, weekend: 100, ph: 100 });
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({ weekday_day: 90, weekday_night: 90, weekend: 100, ph: 100 });

  useEffect(() => {
    fetchNotifications();
    fetchPricingConfig();
  }, []);

  const fetchPricingConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing-config`);
      setPricingConfig(res.data);
      setPricingForm(res.data);
    } catch {
      // Use defaults
    }
  };

  const handleSavePricing = async () => {
    try {
      const res = await axios.put(`${API}/admin/pricing-config`, pricingForm);
      const msg = res.data.bookings_updated > 0
        ? `Pricing updated. ${res.data.bookings_updated} pending booking(s) recalculated.`
        : "Pricing updated successfully";
      toast.success(msg);
      setPricingConfig({...pricingConfig, ...pricingForm});
      setEditingPricing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pricing");
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`);
      setNotifications(res.data || []);
    } catch {
      // silent
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.message.trim()) { toast.error("Message is required"); return; }
    try {
      await axios.post(`${API}/admin/notifications`, notifForm);
      toast.success("Notification created");
      setNotifForm({ message: "", facility: "both" });
      setShowNotifForm(false);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create notification");
    }
  };

  const handleToggleActive = async (notif) => {
    try {
      await axios.put(`${API}/admin/notifications/${notif.notification_id}`, { is_active: !notif.is_active });
      toast.success(notif.is_active ? "Notification disabled" : "Notification enabled");
      fetchNotifications();
    } catch {
      toast.error("Failed to update notification");
    }
  };

  const handleDeleteNotification = async (notifId) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await axios.delete(`${API}/admin/notifications/${notifId}`);
      toast.success("Notification deleted");
      fetchNotifications();
    } catch {
      toast.error("Failed to delete notification");
    }
  };

  const handleStartEdit = (notif) => {
    setEditingNotif(notif.notification_id);
    setEditForm({ message: notif.message, facility: notif.facility });
  };

  const handleSaveEdit = async (notifId) => {
    if (!editForm.message.trim()) { toast.error("Message is required"); return; }
    try {
      await axios.put(`${API}/admin/notifications/${notifId}`, editForm);
      toast.success("Notification updated");
      setEditingNotif(null);
      fetchNotifications();
    } catch {
      toast.error("Failed to update notification");
    }
  };

  const facilityLabel = (f) => f === 'both' ? 'Karaoke & BBQ' : f === 'karaoke' ? 'Karaoke' : 'BBQ Pit';
  const facilityBadge = (f) => {
    if (f === 'both') return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">Both</span>;
    if (f === 'karaoke') return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-bold">Karaoke</span>;
    return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-bold">BBQ Pit</span>;
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border-2 border-blue-300 p-4">
        <p className="text-sm text-blue-800">
          <strong>📅 Shared Calendar Settings:</strong> Public Holidays and Blackout Dates apply to <strong>both</strong> <span className="notranslate" translate="no">Karaoke</span> Room and <span className="notranslate" translate="no">BBQ Pits</span>. 
          Updates here will be reflected across all facility calendars.
        </p>
      </div>

      {/* Booking Notifications */}
      <div className="brutal-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Bell size={22} className="text-amber-500" /> Booking Notifications
          </h3>
          <button
            onClick={() => setShowNotifForm(true)}
            className="px-3 py-2 bg-amber-500 text-white font-bold text-xs border-2 border-black hover:bg-amber-600 flex items-center gap-1"
            data-testid="add-notification-btn"
          >
            <Plus size={14} /> ADD NOTIFICATION
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Create messages that appear as a popup when visitors click on the <span className="notranslate" translate="no">Karaoke</span> or <span className="notranslate" translate="no">BBQ Pit</span> booking card. Use this for payment changes, important notices, or announcements.
        </p>

        {showNotifForm && (
          <form onSubmit={handleCreateNotification} className="bg-amber-50 p-4 border-2 border-amber-300 mb-4" data-testid="notification-form">
            <div className="mb-3">
              <label className="block text-xs font-bold mb-1">Show on</label>
              <div className="flex gap-2 flex-wrap">
                {['karaoke', 'bbq', 'both'].map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setNotifForm({ ...notifForm, facility: f })}
                    className={`px-3 py-1.5 text-sm font-bold border-2 transition-all ${
                      notifForm.facility === f
                        ? 'border-amber-500 bg-amber-200 text-amber-800'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {facilityLabel(f)}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-bold mb-1">Message</label>
              <textarea
                className="brutal-input w-full min-h-[100px]"
                placeholder="Enter notification message..."
                value={notifForm.message}
                onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
                required
                data-testid="notification-message-input"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="brutal-btn-primary text-sm" data-testid="save-notification-btn">Save Notification</button>
              <button type="button" onClick={() => { setShowNotifForm(false); setNotifForm({ message: "", facility: "both" }); }} className="brutal-btn-outline text-sm">Cancel</button>
            </div>
          </form>
        )}

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {notifications.map(n => (
            <div key={n.notification_id} className={`p-3 border-2 rounded ${n.is_active ? 'bg-white border-amber-300' : 'bg-gray-50 border-gray-200 opacity-60'}`} data-testid={`notification-item-${n.notification_id}`}>
              {editingNotif === n.notification_id ? (
                <div className="space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    {['karaoke', 'bbq', 'both'].map(f => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, facility: f })}
                        className={`px-2 py-1 text-xs font-bold border-2 transition-all ${
                          editForm.facility === f
                            ? 'border-amber-500 bg-amber-200 text-amber-800'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {facilityLabel(f)}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="brutal-input w-full min-h-[80px]"
                    value={editForm.message}
                    onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => handleSaveEdit(n.notification_id)} className="px-3 py-1 bg-green-500 text-white text-xs font-bold border-2 border-black hover:bg-green-600 flex items-center gap-1">
                      <Save size={12} /> Save
                    </button>
                    <button onClick={() => setEditingNotif(null)} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-bold border-2 border-gray-400 hover:bg-gray-300 flex items-center gap-1">
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {facilityBadge(n.facility)}
                      {n.is_active 
                        ? <span className="text-xs text-green-600 font-bold flex items-center gap-1"><Bell size={12} /> Active</span>
                        : <span className="text-xs text-gray-400 font-bold flex items-center gap-1"><BellOff size={12} /> Disabled</span>
                      }
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-1">by {n.created_by || 'Admin'}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => handleToggleActive(n)} className={`p-1.5 rounded border-2 ${n.is_active ? 'border-amber-400 text-amber-600 hover:bg-amber-50' : 'border-green-400 text-green-600 hover:bg-green-50'}`} title={n.is_active ? 'Disable' : 'Enable'}>
                      {n.is_active ? <BellOff size={14} /> : <Bell size={14} />}
                    </button>
                    <button onClick={() => handleStartEdit(n)} className="p-1.5 rounded border-2 border-blue-400 text-blue-600 hover:bg-blue-50" title="Edit">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDeleteNotification(n.notification_id)} className="p-1.5 rounded border-2 border-red-400 text-red-600 hover:bg-red-50" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {notifications.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No booking notifications configured</p>}
        </div>
      </div>

      {/* Karaoke Pricing Config */}
      <div className="brutal-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <DollarSign size={22} className="text-green-600" /> Karaoke Pricing
          </h3>
          {!editingPricing ? (
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.put(`${API}/admin/pricing-config`, pricingConfig);
                    const n = res.data.bookings_updated || 0;
                    toast.success(n > 0 ? `${n} pending booking(s) recalculated` : 'All pending bookings already up to date');
                  } catch { toast.error("Failed to recalculate"); }
                }}
                className="px-3 py-2 bg-amber-500 text-white font-bold text-xs border-2 border-black hover:bg-amber-600 flex items-center gap-1"
                data-testid="recalculate-pricing-btn"
              >
                Recalculate Pending
              </button>
              <button
                onClick={() => { setPricingForm({...pricingConfig}); setEditingPricing(true); }}
                className="px-3 py-2 bg-green-600 text-white font-bold text-xs border-2 border-black hover:bg-green-700 flex items-center gap-1"
                data-testid="edit-pricing-btn"
              >
                <Edit2 size={14} /> EDIT PRICING
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleSavePricing} className="px-3 py-2 bg-green-600 text-white font-bold text-xs border-2 border-black hover:bg-green-700 flex items-center gap-1" data-testid="save-pricing-btn">
                <Save size={14} /> SAVE
              </button>
              <button onClick={() => setEditingPricing(false)} className="px-3 py-2 bg-gray-200 text-gray-700 font-bold text-xs border-2 border-gray-400 hover:bg-gray-300 flex items-center gap-1">
                <X size={14} /> CANCEL
              </button>
            </div>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Set karaoke room prices for different day types. Changes apply to all future bookings immediately.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: 'weekday_day', label: 'Weekday Day', sub: 'Mon-Fri (1PM-5PM)', color: 'blue' },
            { key: 'weekday_night', label: 'Weekday Night', sub: 'Mon-Fri (6PM-10PM)', color: 'indigo' },
            { key: 'weekend', label: 'Weekend', sub: 'Sat & Sun', color: 'purple' },
            { key: 'ph', label: 'Public Holiday', sub: 'PH & In-Lieu', color: 'red' },
          ].map(({ key, label, sub, color }) => (
            <div key={key} className={`p-4 border-2 border-${color}-300 bg-${color}-50 text-center`}>
              <p className="text-xs font-bold text-gray-600 mb-1">{label}</p>
              <p className="text-[10px] text-gray-400 mb-2">{sub}</p>
              {editingPricing ? (
                <div className="flex items-center justify-center gap-1">
                  <span className="font-bold">$</span>
                  <input
                    type="number"
                    min="0"
                    value={pricingForm[key]}
                    onChange={(e) => setPricingForm({...pricingForm, [key]: parseInt(e.target.value) || 0})}
                    className="w-20 text-center text-xl font-bold border-2 border-black p-1"
                    data-testid={`pricing-input-${key}`}
                  />
                </div>
              ) : (
                <p className={`text-2xl font-bold text-${color}-700`}>${pricingConfig[key]}</p>
              )}
            </div>
          ))}
        </div>
        {pricingConfig.updated_by && (
          <p className="text-xs text-gray-400 mt-3 text-right">Last updated by {pricingConfig.updated_by}</p>
        )}
      </div>

      {/* Public Holidays */}
      <div className="brutal-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold">🎉 Public Holidays</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFetchApiForm(true)}
              className="px-3 py-2 bg-blue-500 text-white font-bold text-xs border-2 border-black hover:bg-blue-600 flex items-center gap-1"
            >
              <Globe size={14} /> IMPORT FROM API
            </button>
            <button
              onClick={() => setShowHolidayForm(true)}
              className="px-3 py-2 bg-green-500 text-white font-bold text-xs border-2 border-black hover:bg-green-600 flex items-center gap-1"
            >
              <Plus size={14} /> ADD MANUAL
            </button>
          </div>
        </div>
        
        {showHolidayForm && (
          <form onSubmit={onAddHoliday} className="bg-green-50 p-4 border-2 border-green-300 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="date"
                className="brutal-input"
                value={holidayData.date}
                onChange={(e) => setHolidayData({...holidayData, date: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Description (e.g. Chinese New Year)"
                className="brutal-input"
                value={holidayData.description}
                onChange={(e) => setHolidayData({...holidayData, description: e.target.value})}
                required
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button type="submit" className="brutal-btn-primary text-sm">Save Holiday</button>
              <button type="button" onClick={() => setShowHolidayForm(false)} className="brutal-btn-outline text-sm">Cancel</button>
            </div>
          </form>
        )}
        
        {showFetchApiForm && (
          <div className="bg-blue-50 p-4 border-2 border-blue-300 mb-4">
            <p className="text-sm mb-2 font-bold">Import Singapore public holidays from Calendarific API:</p>
            <div className="flex gap-2 items-center flex-wrap">
              <input
                type="number"
                className="brutal-input w-24"
                value={fetchApiYear}
                onChange={(e) => setFetchApiYear(parseInt(e.target.value))}
                min={2024}
                max={2035}
              />
              <button
                onClick={onFetchHolidaysFromApi}
                disabled={fetchingFromApi}
                className="brutal-btn-primary text-sm"
              >
                {fetchingFromApi ? 'Importing...' : 'Import Holidays'}
              </button>
              <button type="button" onClick={() => setShowFetchApiForm(false)} className="brutal-btn-outline text-sm">Cancel</button>
            </div>
          </div>
        )}
        
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {holidays.map(h => (
            <div key={h.holiday_id || h.date} className="flex justify-between items-center p-3 bg-amber-50 border border-amber-200">
              <div>
                <span className="font-mono font-bold">{formatDate(h.date)}</span>
                <span className="ml-3 text-sm">{h.description}</span>
              </div>
              <button onClick={() => onDeleteHoliday(h.holiday_id || h.date)} className="text-red-500 hover:text-red-700">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {holidays.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No public holidays configured</p>}
        </div>
      </div>
      
      {/* Blackout Dates */}
      <div className="brutal-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold">🚫 Blackout Dates</h3>
          <button
            onClick={() => setShowBlackoutForm(true)}
            className="px-3 py-2 bg-red-500 text-white font-bold text-xs border-2 border-black hover:bg-red-600 flex items-center gap-1"
          >
            <Plus size={14} /> ADD BLACKOUT
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Blackout dates prevent bookings during the specified period. You can choose to block specific facilities or both.
        </p>
        
        {showBlackoutForm && (
          <form onSubmit={onAddBlackout} className="bg-red-50 p-4 border-2 border-red-300 mb-4">
            <div className="mb-4">
              <label className="block text-xs font-bold mb-2">Facility Selection</label>
              <div className="flex flex-wrap gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 border-2 cursor-pointer transition-all ${
                  blackoutData.facilities?.includes('karaoke') 
                    ? 'border-purple-500 bg-purple-100 text-purple-800' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={blackoutData.facilities?.includes('karaoke')}
                    onChange={() => onToggleBlackoutFacility('karaoke')}
                    className="hidden"
                  />
                  <span className="text-lg">🎤</span>
                  <span className="font-bold text-sm">Karaoke Booking</span>
                </label>
                
                <label className={`flex items-center gap-2 px-4 py-2 border-2 cursor-pointer transition-all ${
                  blackoutData.facilities?.includes('bbq') 
                    ? 'border-orange-500 bg-orange-100 text-orange-800' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}>
                  <input
                    type="checkbox"
                    checked={blackoutData.facilities?.includes('bbq')}
                    onChange={() => onToggleBlackoutFacility('bbq')}
                    className="hidden"
                  />
                  <span className="text-lg">🔥</span>
                  <span className="font-bold text-sm notranslate" translate="no">BBQ Pits</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {blackoutData.facilities?.length === 2 
                  ? "✓ Both facilities will be blocked" 
                  : blackoutData.facilities?.includes('karaoke') 
                  ? "✓ Only Karaoke Booking will be blocked (BBQ remains open)"
                  : "✓ Only BBQ Pits will be blocked (Karaoke remains open)"}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1">Start Date</label>
                <input type="date" className="brutal-input w-full" value={blackoutData.start_date}
                  onChange={(e) => setBlackoutData({...blackoutData, start_date: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">End Date</label>
                <input type="date" className="brutal-input w-full" value={blackoutData.end_date}
                  onChange={(e) => setBlackoutData({...blackoutData, end_date: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">Reason (shown on calendar)</label>
                <input type="text" placeholder="e.g. RN Residents' Day" className="brutal-input w-full" value={blackoutData.reason}
                  onChange={(e) => setBlackoutData({...blackoutData, reason: e.target.value})} required />
              </div>
            </div>
            
            {blackoutConflicts.length > 0 && (
              <div className="mt-3 p-3 bg-amber-100 border border-amber-400 text-sm">
                <p className="font-bold text-amber-800">⚠️ Conflicts Found:</p>
                {blackoutConflicts.map((c, i) => (
                  <p key={i} className="text-amber-700">{c.facility}: {c.booking_code} - {formatDate(c.date)}</p>
                ))}
              </div>
            )}
            
            <div className="flex gap-2 mt-3">
              <button type="submit" className="brutal-btn-primary text-sm">Save Blackout</button>
              <button type="button" onClick={() => { setShowBlackoutForm(false); setBlackoutConflicts([]); setBlackoutData({ start_date: "", end_date: "", reason: "", facilities: ["karaoke", "bbq"] }); }} className="brutal-btn-outline text-sm">Cancel</button>
            </div>
          </form>
        )}
        
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {blackoutDates.map(b => {
            const facilities = b.facilities || ['karaoke', 'bbq'];
            const hasKaraoke = facilities.includes('karaoke');
            const hasBbq = facilities.includes('bbq');
            
            return (
              <div key={b.blackout_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-red-50 border border-red-200">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold">{b.start_date}</span>
                    <span>→</span>
                    <span className="font-mono font-bold">{b.end_date}</span>
                    <span className="flex items-center gap-1 ml-2">
                      {hasKaraoke && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-bold" title="Karaoke Booking">🎤</span>}
                      {hasBbq && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-bold" title="BBQ Pits">🔥</span>}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{b.reason}</p>
                </div>
                <button onClick={() => onDeleteBlackout(b.blackout_id)} className="text-red-500 hover:text-red-700 self-start sm:self-center">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
          {blackoutDates.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No blackout periods configured</p>}
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="brutal-card bg-gradient-to-r from-blue-50 to-purple-50">
        <h3 className="text-lg font-bold mb-4">📊 Calendar Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-amber-600">{holidays.length}</p>
            <p className="text-xs text-gray-500">Public Holidays</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-red-600">{blackoutDates.length}</p>
            <p className="text-xs text-gray-500">Blackout Periods</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-purple-600">{bookings.length}</p>
            <p className="text-xs text-gray-500">Karaoke Bookings</p>
          </div>
          <div className="text-center p-4 bg-white border-2 border-black">
            <p className="text-2xl font-bold text-orange-600">{bbqBookings.length}</p>
            <p className="text-xs text-gray-500">BBQ Bookings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCalendarTab;
